import { Form } from '@/components';
import {
  useCategoryLookupSource,
  useProjectStepLookupSource,
  useReportingHomesLookupSource,
  useReportingProjectsLookupSource,
} from '@/views';
import { Col, Divider, Modal, Row, Button } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PersonalizationSummaryDownload.module.scss';
import * as Yup from 'yup';
import { Language, ReportOrientationType, ReportSizeType } from '@/core';
import { ReportingClientSelect } from '@/views/Reports.PersonalizationSummary/ReportingClientSelect';
import { ReportingCommunitySelect } from '@/views/Reports.PersonalizationSummary/ReportingCommunitySelect';
import i18next from 'i18next';

export const ORIENTATION_OPTIONS = [
  {
    value: 'Portrait',
    label: i18next.t<string>(
      'personalization.downloadPdf.orientationOptions.portrait',
    ),
  },
  {
    value: 'Landscape',
    label: i18next.t<string>(
      'personalization.downloadPdf.orientationOptions.landscape',
    ),
  },
];

export const SIZE_OPTIONS = [
  {
    value: 'Letter',
    label: i18next.t<string>('personalization.downloadPdf.sizeOptions.letter'),
  },
  {
    value: 'Tabloid',
    label: i18next.t<string>('personalization.downloadPdf.sizeOptions.tabloid'),
  },
];

export const LANGUAGE_OPTIONS = [
  {
    value: Language.English,
    label: i18next.t<string>('enums.Language.English'),
  },
  {
    value: Language.Spanish,
    label: i18next.t<string>('enums.Language.Spanish'),
  },
];

const schema = Yup.object().shape({
  _required: Yup.boolean().nullable().required(),
  clientIds: Yup.array().of(Yup.string().defined()),
  communityIds: Yup.array().of(Yup.string().defined()),
  projectIds: Yup.array()
    .of(Yup.string().nullable().required())
    .nullable()
    .defined()
    .required()
    .when('_required', {
      is: true,
      then: Yup.array().min(1).required(),
    }),
  homeIds: Yup.array()
    .of(Yup.string().nullable().required())
    .nullable()
    .defined()
    .required()
    .when('_required', {
      is: true,
      then: Yup.array().min(1).required(),
    }),
  stepIds: Yup.array()
    .of(Yup.string().nullable().required())
    .nullable()
    .defined(),
  categoryIds: Yup.array()
    .of(Yup.string().nullable().required())
    .nullable()
    .defined(),
  language: Yup.mixed<Language>().required(),
  orientation: Yup.mixed<ReportOrientationType>().required(),
  size: Yup.mixed<ReportSizeType>().required(),
  showResidentInfo: Yup.boolean().defined(),
  showConstruction: Yup.boolean().defined(),
  showBaseCost: Yup.boolean().defined(),
  showResidentPrice: Yup.boolean().defined(),
  showSignature: Yup.boolean().defined(),
  showFloorplanImage: Yup.boolean().defined(),
  showInactiveItems: Yup.boolean().defined(),
  showItemDescription: Yup.boolean().defined(),
  showConstructionDescription: Yup.boolean().defined(),
});

export type PersonalizationSummaryFormValue = OmitIndex<
  Yup.InferType<typeof schema>
>;

export const PERSONALIZATION_SUMMARY_FORM_INITIAL_VALUE: PersonalizationSummaryFormValue =
  {
    _required: false,
    clientIds: [],
    communityIds: [],
    projectIds: [],
    homeIds: [],
    stepIds: [],
    categoryIds: [],
    language: Language.English,
    orientation: ReportOrientationType.Portrait,
    size: ReportSizeType.Letter,
    showResidentInfo: false,
    showConstruction: false,
    showBaseCost: false,
    showResidentPrice: true,
    showSignature: true,
    showFloorplanImage: true,
    showInactiveItems: true,
    showItemDescription: true,
    showConstructionDescription: false,
  };

export type PersonalizationSummaryFilterByType =
  | 'client'
  | 'community'
  | 'project'
  | 'home'
  | 'step'
  | 'category'
  | 'baseCost';

export interface DownloadPersonalizationSummaryPdfModalProps {
  open: boolean;
  title: string;
  layout?: 'one-column' | 'two-columns';
  downloadButtonTitle: string;
  downloadButtonIcon?: React.ReactNode;
  onClose: () => void;
  onDownload: (
    values: PersonalizationSummaryFormValue,
    onClose: () => void,
  ) => void;
  initialValue?: PersonalizationSummaryFormValue;
  filterBy?: PersonalizationSummaryFilterByType[];
}

export function DownloadPersonalizationSummaryPdfModal(
  props: DownloadPersonalizationSummaryPdfModalProps,
) {
  const {
    open,
    title,
    layout = 'two-columns',
    downloadButtonTitle,
    downloadButtonIcon,
    onClose,
    onDownload,
    initialValue,
    filterBy = [
      'client',
      'community',
      'project',
      'home',
      'step',
      'category',
      'baseCost',
    ],
  } = props;
  const isTwoColumns = layout === 'two-columns';
  const { t } = useTranslation();

  return (
    <Modal
      visible={open}
      title={title}
      width={isTwoColumns ? 1000 : 600}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('cancel')}
        </Button>,
        <Form.Submit
          key="download"
          uid="download-personalization-summary-pdf"
          type="primary"
          icon={downloadButtonIcon}
        >
          {downloadButtonTitle}
        </Form.Submit>,
      ]}
    >
      <Form.Formik<PersonalizationSummaryFormValue>
        uid="download-personalization-summary-pdf"
        i18n="personalization.downloadPdf"
        initialValues={
          initialValue ?? PERSONALIZATION_SUMMARY_FORM_INITIAL_VALUE
        }
        validationSchema={schema}
        onSubmit={(values) => {
          onDownload(values, onClose);
        }}
        enableReinitialize
      >
        <FilterForm isTwoColumns={isTwoColumns} filterBy={filterBy} />
      </Form.Formik>
    </Modal>
  );
}

function FilterForm(props: {
  isTwoColumns: boolean;
  filterBy: PersonalizationSummaryFilterByType[];
}) {
  const { isTwoColumns, filterBy } = props;
  const { values, setFieldValue } =
    useFormikContext<PersonalizationSummaryFormValue>();
  const { projectIds: projectId, clientIds, communityIds } = values;
  const { t } = useTranslation();

  const projectsDataSource = useReportingProjectsLookupSource(
    true,
    communityIds,
    clientIds,
  );

  const homesDataSource = useReportingHomesLookupSource(
    clientIds,
    communityIds,
    projectId,
    true,
  );

  const stepsDataSource = useProjectStepLookupSource(projectId, 'subtitle');

  const categoriesDataSource = useCategoryLookupSource({
    communityId: communityIds ? communityIds[0] : undefined,
    listed: true,
  });

  useEffect(() => {
    if (!projectId?.length) {
      setFieldValue('homeIds', []);
      setFieldValue('stepIds', []);
      return;
    }

    Promise.resolve(stepsDataSource.search(''))
      .then((steps) => {
        const allStepIds = steps?.map((s) => s.value) ?? [];
        setFieldValue('stepIds', allStepIds);
      })
      .catch(() => {
        setFieldValue('stepIds', []);
      });

    setFieldValue('homeIds', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setFieldValue('projectIds', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityIds]);

  useEffect(() => {
    setFieldValue('communityIds', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds]);
  const isLetterSize = useMemo(
    () => values.size === ReportSizeType.Letter,
    [values.size],
  );

  return (
    <Row>
      <Col span={isTwoColumns ? 12 : 24}>
        {filterBy.includes('client') && (
          <ReportingClientSelect name="clientIds" mode="multiple" />
        )}

        {filterBy.includes('community') && (
          <ReportingCommunitySelect
            name="communityIds"
            clientId={clientIds}
            mode="multiple"
          />
        )}

        {filterBy.includes('project') && (
          <Form.AsyncSelect
            key={communityIds?.join()}
            name="projectIds"
            dataSource={projectsDataSource}
            mode="multiple"
            preload
            required
          />
        )}

        {filterBy.includes('home') && (
          <Form.AsyncSelect
            key={'home' + projectId?.join()}
            name="homeIds"
            dataSource={homesDataSource}
            mode="multiple"
            required
            preload
            disabled={!projectId.length}
          />
        )}

        {filterBy.includes('step') && (
          <Form.AsyncSelect
            key={'stepIds' + projectId?.join()}
            name="stepIds"
            dataSource={stepsDataSource}
            mode="multiple"
            disabled={!projectId.length}
            preload
          />
        )}

        {filterBy.includes('category') && (
          <Form.AsyncSelect
            dataSource={categoriesDataSource}
            name="categoryIds"
            mode="multiple"
            preload
          />
        )}

        <Divider />

        <Form.Radio name="size" options={SIZE_OPTIONS} inline={false} />
        <Divider />

        {isLetterSize && (
          <>
            <Form.Radio
              name="orientation"
              options={ORIENTATION_OPTIONS}
              inline={false}
            />
            <Divider />
          </>
        )}

        <Form.Radio name="language" options={LANGUAGE_OPTIONS} inline={false} />
        <Divider />

        <Form.Group
          name="showConstructionDescription"
          label={t('personalization.downloadPdf.description')}
          inline={false}
        >
          <Form.Checkbox
            name="showItemDescription"
            label={t('personalization.downloadPdf.descriptionOptions.item')}
          />
          <Form.Checkbox
            name="showConstructionDescription"
            label={t(
              'personalization.downloadPdf.descriptionOptions.construction',
            )}
          />
        </Form.Group>
      </Col>

      {!isTwoColumns && (
        <Col span={24}>
          <Divider />
          <RightColumnContent filterBy={filterBy} />
        </Col>
      )}
      {isTwoColumns && (
        <Col span={12} className={styles.secondColumn}>
          <RightColumnContent filterBy={filterBy} />
        </Col>
      )}
    </Row>
  );
}

const RightColumnContent = (props: {
  filterBy: PersonalizationSummaryFilterByType[];
}) => {
  const { filterBy } = props;
  const { t } = useTranslation();
  return (
    <>
      <Form.Switch
        name="showSignature"
        label={t('personalization.downloadPdf.signature')}
      />
      <Form.Checkbox
        name="showConstruction"
        label={t('personalization.downloadPdf.showConstruction')}
      />
      <Form.Checkbox
        name="showResidentInfo"
        label={t('personalization.summary.showResidentInfo')}
      />
      {filterBy.includes('baseCost') && (
        <Form.Checkbox
          name="showBaseCost"
          label={t('personalization.summary.showBaseCost')}
        />
      )}
      <Form.Checkbox
        name="showResidentPrice"
        label={t('personalization.summary.showResidentPrice')}
      />
      <Form.Checkbox
        name="showFloorplanImage"
        label={t('personalization.summary.showFloorplanImage')}
      />
      <Form.Checkbox
        name="showInactiveItems"
        label={t('personalization.summary.showInactiveItems')}
      />
    </>
  );
};
